import React, { useState } from 'react';
import axios from 'axios';
import { KeenIcon } from '@/components';
import clsx from "clsx";
import {enqueueSnackbar} from "notistack";

interface IBenefitsProps {
    title: string;
    items: string[];
    className?: string;
    job_p_id: string; // Assuming jobId is needed for backend updates
}

const Benefits = ({ title, items, className, job_p_id }: IBenefitsProps) => {
    const [isEditing, setIsEditing] = useState<boolean>(false);
    const [editedItems, setEditedItems] = useState<string[]>([...items]);
    const [newBenefit, setNewBenefit] = useState<string>('');
    const [loading, setLoading] = useState<boolean>(false);

    const API_URL = import.meta.env.VITE_APP_API_URL;

    const handleEditClick = () => setIsEditing(true);

    const handleCancelClick = () => {
        setEditedItems([...items]);
        setNewBenefit('');
        setIsEditing(false);
    };

    const handleSaveClick = async () => {
        setLoading(true);

        try {
            const token = localStorage.getItem('authToken'); // Adjust token retrieval as needed
            await axios.put(
                `${API_URL}/job/${job_p_id}/benefits`,
                { benefits: editedItems },
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                        'Content-Type': 'application/json',
                    },
                }
            );
            setIsEditing(false);
            // Optionally, trigger a parent component update or refresh items here
        } catch (err) {
            enqueueSnackbar(`Failed to update benefits: ${err}`, {
                variant: 'solid',
                state: 'danger'
            });
        } finally {
            setLoading(false);
        }
    };

    const handleAddBenefit = () => {
        const trimmedBenefit = newBenefit.trim();
        if (trimmedBenefit && !editedItems.includes(trimmedBenefit)) {
            setEditedItems([...editedItems, trimmedBenefit]);
            setNewBenefit('');
        }
    };

    const handleEditBenefit = (value: string, index: number) => {
        const updatedItems = [...editedItems];
        updatedItems[index] = value;
        setEditedItems(updatedItems);
    };

    const handleDeleteBenefit = (index: number) => {
        const updatedItems = editedItems.filter((_, i) => i !== index);
        setEditedItems(updatedItems);
    };

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setNewBenefit(e.target.value);
    };

    const renderItemView = (item: string, index: number) => (
        <div key={index} className="flex items-center gap-2.5 w-full">
            <span>
                <KeenIcon icon="like" className="text-primary text-sm" />
            </span>
            <span className="flex-1 text-sm">{item}</span>
        </div>
    );

    const renderItemEdit = (item: string, index: number) => (
        <div key={index} className="flex items-center gap-2.5 w-full mb-2">
            <input
                type="text"
                value={item}
                onChange={(e) => handleEditBenefit(e.target.value, index)}
                className="input input-bordered input-sm flex-1"
                placeholder="Edit benefit"
            />
            <button
                type="button"
                className="btn btn-ghost btn-sm text-red-500"
                onClick={() => handleDeleteBenefit(index)}
                aria-label={`Delete ${item}`}
            >
                &times;
            </button>
        </div>
    );

    return (
        <div className={clsx('card', className && className)}>
            <div className="card-header flex justify-between items-center">
                <h3 className="card-title">{title}</h3>
                {!isEditing ? (
                    <button className="btn btn-light btn-sm" onClick={handleEditClick}>
                        <KeenIcon icon="notepad-edit" />
                        Edit
                    </button>
                ) : (
                    <div className="flex space-x-2">
                        <button className="btn btn-primary btn-sm" onClick={handleSaveClick} disabled={loading}>
                            <KeenIcon icon="file-up" />
                            {loading ? 'Saving...' : 'Save'}
                        </button>
                        <button className="btn btn-secondary btn-sm" onClick={handleCancelClick} disabled={loading}>
                            <KeenIcon icon="tablet-delete" />
                            Cancel
                        </button>
                    </div>
                )}
            </div>

            <div className="card-body">

                <div className={`flex flex-wrap gap-2.5 mb-4 ${isEditing ? 'flex-col' : ''}`}>
                    {isEditing ? (
                        editedItems.map((item, index) => renderItemEdit(item, index))
                    ) : (
                        editedItems.map((item, index) => renderItemView(item, index))
                    )}
                </div>

                {isEditing && (
                    <div className="flex items-center space-x-2">
                        <input
                            type="text"
                            value={newBenefit}
                            onChange={handleInputChange}
                            placeholder="Add new benefit"
                            className="input input-bordered input-sm flex-1"
                            onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                    e.preventDefault();
                                    handleAddBenefit();
                                }
                            }}
                        />
                        <button
                            type="button"
                            className="btn btn-success btn-sm"
                            onClick={handleAddBenefit}
                            disabled={!newBenefit.trim()}
                        >
                            <KeenIcon icon="add-notepad" />
                            Add
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
};

export { Benefits, type IBenefitsProps };
